import { oc } from 'ts-optchain';
import Config from 'react-native-config';
import { AnyAction, Dispatch } from 'redux';
import { ReduxAxiosAction } from 'redux-axios-middleware';
import {
  createInsuranceVerificationRequestBody,
  InsuranceVerificationRequestDto
} from 'lib/insurance';

import { RootState } from 'store/types';
import { KEY } from 'store/booking/types';
import * as profileSelectors from 'store/profile/selectors';
import { currentPatientIdSelector } from 'store/booking/appointment/selectors';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import {
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherEndpoint,
  DispatcherResource
} from 'modules/types/common';

export const VERIFY_INSURANCE = `${KEY}.VERIFY_INSURANCE`;
export const VERIFY_INSURANCE_FAIL = `${KEY}.VERIFY_INSURANCE_FAIL`;
export const VERIFY_INSURANCE_SUCCESS = `${KEY}.VERIFY_INSURANCE_SUCCESS`;

const url = getDispatcherUrl(
  DispatcherCategoryService.BILLING,
  DispatcherAuthPrefix.AUTHENTICATED,
  DispatcherResource.INSURANCE,
  DispatcherEndpoint.PLANS_STATUS
);

export const verifyInsurance = (data: InsuranceVerificationRequestDto): ReduxAxiosAction => ({
  type: VERIFY_INSURANCE,
  payload: {
    client: 'dispatcher',
    request: {
      timeout: 120 * 1000, // Double the timeout here because this request takes forever
      headers: { facilityCode: Config.INSURANCE_VERIFICATION_FACILITY_CODE },
      method: 'POST',
      url,
      data
    }
  }
});

export const PATIENT_INSURANCE_VERIFICATION_STARTED = `${KEY}.PATIENT_INSURANCE_VERIFICATION_STARTED`;
export const PATIENT_INSURANCE_VERIFICATION_ERROR = `${KEY}.PATIENT_INSURANCE_VERIFICATION_ERROR`;
export const PATIENT_INSURANCE_VERIFICATION_COMPLETED = `${KEY}.PATIENT_INSURANCE_VERIFICATION_COMPLETED`;

export const validateSelectedPatientInsurance = (id?: string) => async (
  dispatch: Dispatch<AnyAction>,
  getState: () => RootState
) => {
  // Select Current consumer
  const state = getState();
  const profileConsumer = profileSelectors.profileConsumerSelector(state);
  const profileInsurances = profileSelectors.profileInsurancesSelector(state);
  const bookingPatientId = id || currentPatientIdSelector(state);
  const isCurrentUser = profileConsumer.consumerId === bookingPatientId;

  if (!isCurrentUser) {
    return;
  }

  dispatch({ type: PATIENT_INSURANCE_VERIFICATION_STARTED });

  const primaryMilenniumInsurance = profileInsurances.find(
    insurance =>
      insurance.source === 'Millennium' &&
      insurance.primary === true &&
      insurance.members?.some(member => member.consumerId === profileConsumer.consumerId)
  );

  if (!primaryMilenniumInsurance) {
    dispatch({
      type: PATIENT_INSURANCE_VERIFICATION_ERROR,
      error: 'No insurance on file to verify.'
    });
    return;
  }

  const response = await dispatch(
    verifyInsurance(
      createInsuranceVerificationRequestBody(primaryMilenniumInsurance, profileConsumer)
    )
  );

  if (response.error) {
    dispatch({
      type: PATIENT_INSURANCE_VERIFICATION_ERROR,
      error: 'Failed to verify insurance.'
    });
    return;
  }

  dispatch({
    type: PATIENT_INSURANCE_VERIFICATION_COMPLETED,
    payload: oc(response).payload.data.isValid(false)
  });
};
